import { ApiService } from './api.service';
import {
    IAcceptGuidanceValue,
    ICreateReport,
    IReport,
    IReportDocument,
    ISelectLanguageForReport,
    IUpdateReport,
    IUpdateReportMeta,
    IUser,
    ReportStatus,
    ResponsibleType
} from '@shared/interfaces';
import { Injectable } from '@angular/core';
import { GuardService } from './guard.service';
import pixelWidth from 'string-pixel-width';
import { sign } from '../helpers/signature';
import html2canvas from 'html2canvas';
import { IQueryReport } from '../interfaces/queries/IQueryReport';
import { convertQueryToParams } from '../helpers/query-converter';
import { IReportLight } from '../interfaces/reports/IReportLight';
import { IPaginatedResult } from '@shared/interfaces/mailing/IPaginatedResult';
import { FileSaverService } from 'ngx-filesaver';
import { UserService } from './user.service';
import { FileService } from './file.service';
import { v4 } from 'uuid';
import { IS_PRODUCT_SAFETY_PROJECT, IS_TRS_PROJECT } from '../../../current-project';

@Injectable({
    providedIn: 'root'
})
export class ReportService {
    constructor(
        private apiService: ApiService,
        private guardService: GuardService,
        private fileSaver: FileSaverService,
        private userService: UserService,
        private fileService: FileService
    ) {}

    public async getItem(reportId: string): Promise<IReport> {
        const item = (await this.apiService.get(`/reports/by-id?id=${encodeURIComponent(reportId)}`)) as IReport;

        return item;
    }

    getItemsLight(query: IQueryReport): Promise<Array<IReportLight>> {
        return this.apiService.get(`/reports/light${convertQueryToParams<IQueryReport>(query)}`);
    }

    async getAllItems(): Promise<IReport[]> {
        try {
            const data = await this.apiService.get('/reports?responsibleType=all');
            return this.transformItems(data);
        } catch (e) {
            console.error(e);
        }
    }

    async getAllItemsPaginated(): Promise<IReport[]> {
        try {
            const allItems: IReportLight[] = [];
            let page = 0;
            const pageSize = 200;
            let hasMore = true;

            while (hasMore) {
                const query: IQueryReport = {
                    responsibleType: ResponsibleType.all,
                    withResponsible: true,
                    pagination: { page, pageSize }
                };
                const result: IPaginatedResult<IReportLight> = await this.apiService.get(
                    `/reports/light/paginated${convertQueryToParams<IQueryReport>(query)}`
                );
                allItems.push(...(result.items || []));
                hasMore = result.hasMore;
                page++;
            }

            return this.transformItems(allItems);
        } catch (e) {
            console.error(e);
        }
    }

    getMyItems(query: IQueryReport): Promise<IReport[]> {
        return this.apiService.get(`/reports${convertQueryToParams<IQueryReport>(query)}`);
    }

    public createItem(item: ICreateReport): Promise<boolean> {
        return this.apiService.post('/reports', item);
    }

    public updateItemMeta(item: IUpdateReportMeta): Promise<IReport> {
        return this.apiService.patch('/reports/meta', item);
    }

    public resetItem(item: IUpdateReportMeta): Promise<IReport> {
        return this.apiService.patch('/reports/reset', item);
    }

    public updateGuidance(item: IAcceptGuidanceValue): Promise<IReport> {
        return this.apiService.patch('/reports/accept-guidance', item);
    }

    public selectLanguage(item: ISelectLanguageForReport): Promise<IReport> {
        return this.apiService.patch('/reports/select-language', item);
    }

    public async gatherDocuments(report: IReport, item: IUpdateReport) {
        return await new Promise<void>(resolve => {
            if (report.documents && report.documents.length) {
                const documents = report.documents.filter(e => e instanceof File);
                if (documents.length) {
                    const reader = new FileReader();
                    reader.readAsArrayBuffer(documents[0] as File);
                    reader.onload = () => {
                        item.documents = item.documents.filter(e => e.name !== (documents[0] as File).name);
                        item.documents.push({
                            name: (documents[0] as File).name,
                            body: reader.result as any,
                            size: (documents[0] as File).size
                        });
                        resolve();
                    };
                } else {
                    resolve();
                }
            } else {
                resolve();
            }
        });
    }

    public async signAsSpecialist(report: IReport): Promise<IReport> {
        if (IS_TRS_PROJECT) {
            const item: IUpdateReport = {
                id: report.id,
                reportValues: report.reportValues,
                doc: report.doc,
                documents: report.documents,
                deleteDocuments: report.deleteDocuments || [],
                reportStartDate: report.reportStartDate,
                reportEndDate: report.reportEndDate,
                notifyResponsible: report.notifyResponsible
            };

            const signature = await this.createSignature(this.guardService.currentUser, true, report.selectedLanguage);

            await this.gatherDocuments(report, item);
            await this.handleDocuments(item);
            await this.handleSignature(item, 'specialist', this.guardService.currentUser.id, signature);

            return this.apiService.post('/reports/specialist-signature', item);
        }

        if (IS_PRODUCT_SAFETY_PROJECT) {
            const item: IUpdateReport = {
                id: report.id,
                reportValues: report.reportValues,
                doc: report.doc,
                documents: report.documents,
                deleteDocuments: report.deleteDocuments || [],
                signDocument: [
                    await this.createSignature(this.guardService.currentUser, true, report.selectedLanguage)
                ],
                reportStartDate: report.reportStartDate,
                reportEndDate: report.reportEndDate,
                notifyResponsible: report.notifyResponsible
            };

            await this.gatherDocuments(report, item);
            await this.handleDocuments(item);

            return this.apiService.post('/reports/specialist-signature', item);
        }
    }

    public async updateReport(report: IReport): Promise<IReport> {
        if (IS_TRS_PROJECT) {
            const item: IUpdateReport = {
                id: report.id,
                reportValues: report.reportValues,
                doc: report.doc,
                documents: report.documents,
                deleteDocuments: report.deleteDocuments || [],
                reportStartDate: report.reportStartDate,
                reportEndDate: report.reportEndDate
            };

            await this.gatherDocuments(report, item);
            await this.handleDocuments(item);

            return this.apiService.patch('/reports/values', item);
        }

        if (IS_PRODUCT_SAFETY_PROJECT) {
            const item: IUpdateReport = {
                id: report.id,
                reportValues: report.reportValues,
                doc: report.doc,
                documents: report.documents,
                deleteDocuments: report.deleteDocuments || [],
                signDocument: [],
                reportStartDate: report.reportStartDate,
                reportEndDate: report.reportEndDate
            };

            await this.gatherDocuments(report, item);
            await this.handleDocuments(item);

            return this.apiService.patch('/reports/values', item);
        }
    }

    public async signAsResponsible(report: IReport): Promise<IReport> {
        if (IS_TRS_PROJECT) {
            const item: IUpdateReport = {
                id: report.id,
                reportValues: report.reportValues,
                doc: report.doc,
                documents: report.documents,
                deleteDocuments: report.deleteDocuments || [],
                reportStartDate: report.reportStartDate,
                reportEndDate: report.reportEndDate
            };

            const signature = await this.createSignature(this.guardService.currentUser, false, report.selectedLanguage);

            await this.gatherDocuments(report, item);
            await this.handleDocuments(item);
            await this.handleSignature(item, 'responsible', this.guardService.currentUser.id, signature);

            return this.apiService.post('/reports/responsible-signature', item);
        }
        if (IS_PRODUCT_SAFETY_PROJECT) {
            const item: IUpdateReport = {
                id: report.id,
                reportValues: report.reportValues,
                doc: report.doc,
                documents: report.documents,
                deleteDocuments: report.deleteDocuments || [],
                signDocument: [
                    await this.createSignature(this.guardService.currentUser, false, report.selectedLanguage)
                ],
                reportStartDate: report.reportStartDate,
                reportEndDate: report.reportEndDate
            };

            await this.gatherDocuments(report, item);
            await this.handleDocuments(item);

            return this.apiService.post('/reports/responsible-signature', item);
        }
    }

    public async signForResponsible(report: IReport): Promise<IReport> {
        if (IS_TRS_PROJECT) {
            const item: IUpdateReport = {
                id: report.id,
                reportValues: report.reportValues,
                doc: report.doc,
                documents: report.documents,
                deleteDocuments: report.deleteDocuments || [],
                reportStartDate: report.reportStartDate,
                reportEndDate: report.reportEndDate
            };

            const signature = await this.createSignature(this.guardService.currentUser, false, report.selectedLanguage);

            await this.gatherDocuments(report, item);
            await this.handleDocuments(item);
            await this.handleSignature(item, 'responsible', this.guardService.currentUser.id, signature);
            await this.handleSignDocument(item, report.signDocuments[0] as any);

            return this.apiService.post('/reports/acting-responsible-signature', item);
        }

        if (IS_PRODUCT_SAFETY_PROJECT) {
            const item: IUpdateReport = {
                id: report.id,
                reportValues: report.reportValues,
                doc: report.doc,
                documents: report.documents,
                deleteDocuments: report.deleteDocuments || [],
                signDocument: [
                    await this.createSignature(this.guardService.currentUser, false, report.selectedLanguage)
                ],
                reportStartDate: report.reportStartDate,
                reportEndDate: report.reportEndDate
            };

            await this.gatherDocuments(report, item);
            await this.handleDocuments(item);
            await this.handleSignDocument(item, report.signDocuments[0] as any);

            return this.apiService.post('/reports/acting-responsible-signature', item);
        }
    }

    deleteItem(item: IReport): Promise<boolean> {
        return this.apiService.delete(`/reports/by-id?id=${encodeURIComponent(item.id)}`);
    }

    async createSignature(
        user: IUser,
        isSpecialist: boolean,
        reportLanguage: string,
        date = new Date()
    ): Promise<string | ArrayBuffer | any> {
        let signaturePrefix = '';
        let dateName = '';
        if (reportLanguage === 'de') {
            signaturePrefix = isSpecialist ? 'Erstellt und freigegeben von' : 'Geprüft und final freigegeben von';
            dateName = 'Datum:';
        } else {
            signaturePrefix = isSpecialist ? 'Created and approved by' : 'Reviewed and finally approved by';
            dateName = 'Date:';
        }

        return new Promise((resolve, reject) => {
            try {
                const signer = this.splitValues(`${user.userData.fullName}`, 64, 425).map(
                    e => `<tspan x="10" dy="1em">${e}</tspan>`
                );
                const metadata = this.splitValues(
                    `${signaturePrefix} ${user.userData.fullName}, o=Siemens, d=${user.userData.department}, gid=${user.id}, ${dateName} ${date.toLocaleDateString()}`,
                    34,
                    415
                ).map(e => `<tspan x="10" dy="1em">${e}</tspan>`);

                const element = document.createElement('div');
                element.style.position = 'fixed';
                element.innerHTML = sign(signer, metadata);
                document.body.appendChild(element);
                html2canvas(element).then(canvas => {
                    document.body.removeChild(element);
                    const imgData = canvas.toDataURL('image/jpeg');
                    if (IS_PRODUCT_SAFETY_PROJECT) {
                        resolve(imgData.split(',')[1]);
                    } else {
                        const reader = new FileReader();
                        reader.readAsArrayBuffer(this.dataURItoArray(imgData));
                        reader.onload = () => {
                            resolve(reader.result);
                        };
                    }
                });
            } catch (e) {
                console.error(e);
                reject(e);
            }
        });
    }

    splitValues(value: string, size: number, maxWidth: number): string[] {
        const result = [];

        let values: string[] = value.split(' ');
        while (value) {
            for (let i = values.length; i > 0; i--) {
                if (pixelWidth(values.slice(0, i).join(' '), { size }) <= maxWidth) {
                    result.push(values.slice(0, i).join(' '));
                    values = values.slice(i, values.length);
                    value = values.join(' ');
                    break;
                }
            }
        }
        return result;
    }

    dataURItoArray(dataURI: string): Blob {
        const byteString = atob(dataURI.split(',')[1]);
        const mimeString = dataURI.split(',')[0].split(':')[1].split(';')[0];
        const ab = new ArrayBuffer(byteString.length);
        const ia = new Uint8Array(ab);

        for (let i = 0; i < byteString.length; i++) {
            ia[i] = byteString.charCodeAt(i);
        }
        return new Blob([ab], { type: mimeString });
    }

    hasEmbeddedImages(report: IReport | string): boolean {
        if (typeof report !== 'string') {
            return (
                report.reportValues &&
                Object.keys(report.reportValues).some(key => {
                    const parser = new DOMParser();
                    const document = parser.parseFromString(report.reportValues[key].value, 'text/html');
                    return document.getElementsByTagName('img').length > 0;
                })
            );
        } else {
            const parser = new DOMParser();
            const document = parser.parseFromString(report, 'text/html');
            return document.getElementsByTagName('img').length > 0;
        }
    }

    private transformItems(items: IReportLight[]): IReport[] {
        items = items.sort((a, b) => new Date(b.dueDate).getFullYear() - new Date(a.dueDate).getFullYear());
        const result: IReport[] = items
            .filter(
                (a, i, arr) =>
                    i === 0 || new Date(arr[i - 1].dueDate).getFullYear() !== new Date(a.dueDate).getFullYear()
            )
            .map(e => ({
                title: `${new Date(e.dueDate).getFullYear()}`,
                id: e.dueDate
            }));

        for (const period of result.map(e => e.title)) {
            const item = result.find(e => e.title === period);

            const responsibleTypes = items
                .filter(e => `${new Date(e.dueDate).getFullYear()}` === period)
                .sort((a, b) => a.responsible.responsibleType.localeCompare(b.responsible.responsibleType));

            item.children = responsibleTypes
                .filter(
                    (a, i, arr) => i === 0 || arr[i - 1].responsible.responsibleType !== a.responsible.responsibleType
                )
                .map(e => ({
                    title: `${e.responsible.responsibleType}`,
                    responsibleType: e.responsible.responsibleType,
                    id: e.dueDate + e.responsible.responsibleType
                }));

            let draftItems = 0;
            let releasedItems = 0;
            let inProgressItems = 0;
            for (const type of item.children.map(e => e.title)) {
                const item2 = item.children.find(e => e.title === type);

                const titles = responsibleTypes
                    .filter(e => e.responsible.responsibleType === type)
                    .sort((a, b) => a.responsible.responsibleType.localeCompare(b.responsible.responsibleType));

                item2.children = titles
                    .map(e => ({
                        ...e,
                        title: `${e.responsible.orgCode} - ${e.responsible.orgDescription}`,
                        endNode: true
                    }))
                    .sort((a, b) => a.title.localeCompare(b.title));
                item2.totalItems = item2.children.length;
                releasedItems += item2.releasedItems = item2.children.filter(
                    e => e.reportStatus === ReportStatus.released
                ).length;
                draftItems += item2.draftItems = item2.children.filter(
                    e => e.reportStatus === ReportStatus.draft
                ).length;
                inProgressItems += item2.inProgressItems = item2.children.filter(
                    e => e.reportStatus === ReportStatus.inProgress
                ).length;
            }
            item.totalItems = draftItems + releasedItems + inProgressItems;
            item.releasedItems = releasedItems;
            item.draftItems = draftItems;
            item.inProgressItems = inProgressItems;
        }

        return result;
    }

    private async handleDocuments(item: IReport) {
        const newDocuments = item.documents?.filter((e: IReportDocument) => !!e.body);
        if (newDocuments?.length) {
            const uploads = await Promise.all(
                newDocuments.map(async (e: IReportDocument) => {
                    const uniqueId = v4();
                    const uploadLink = await this.fileService.getUploadLink(
                        item.id,
                        `${uniqueId}.${e.name.split('.').pop()}`
                    );
                    return {
                        uploadLink,
                        body: e.body,
                        uniqueName: uniqueId,
                        fileName: e.name
                    };
                })
            );

            await Promise.all(uploads.map(e => this.fileService.uploadAttachment(e.uploadLink?.link, e.body)));

            for (const document of item.documents as IReportDocument[]) {
                document.body = null;
                document.id = uploads.find(doc => doc.fileName === document.name)?.uniqueName;
            }
        }
    }

    private async handleSignDocument(item: IReport, document) {
        const uploads = await Promise.all(
            [document].map(async (e: IReportDocument) => {
                const uploadLink = await this.fileService.getUploadLinkForReport(item.id);
                return { uploadLink, body: e };
            })
        );

        await Promise.all(uploads.map(e => this.fileService.uploadAttachment(e.uploadLink?.link, e.body)));
    }

    private async handleSignature(item: IReport, type: string, signerId: string, document) {
        const uploads = await Promise.all(
            [document].map(async (e: IReportDocument) => {
                const uploadLink = await this.fileService.getUploadLinkForSignature(item.id, type, signerId);
                return { uploadLink, body: e };
            })
        );

        await Promise.all(uploads.map(e => this.fileService.uploadAttachment(e.uploadLink?.link, e.body)));
    }

    async uploadManualReport(item: IReport, document: File) {
        const uploadLink = await this.fileService.getUploadLinkForReport(item.id);
        await this.fileService.uploadAttachment(uploadLink?.link, await document.arrayBuffer());
    }

    async uploadManual(report: IReport): Promise<IReport> {
        return this.apiService.put(`/reports/upload?id=${encodeURIComponent(report.id)}`, {});
    }
}
